import type { EChartsOption } from 'echarts';

import type { BubbleChartConfig, BubbleNodeInput, BubbleNodeMeta } from './types';

export type { BubbleChartConfig, BubbleChartTheme, BubbleNodeInput, BubbleNodeMeta, EChartsShadow } from './types';

// ─── scaling helpers ──────────────────────────────────────────────────────────

export function getValueRange(nodes: BubbleNodeInput[]): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  function traverse(node: BubbleNodeInput) {
    min = Math.min(min, node.value);
    max = Math.max(max, node.value);
    if (Array.isArray(node.children)) node.children.forEach(traverse);
  }
  nodes.forEach(traverse);
  return [min === Infinity ? 0 : min, max === -Infinity ? 1 : max];
}

export function scaleBubbleSymbolSize(value: number, min: number, max: number): number {
  if (max === min) return 140;
  // Sqrt scaling maps value to visual area rather than diameter so equal value differences
  // look proportional on screen (perceptual area scaling, analogous to Flannery's principle).
  const sqrtMin = Math.sqrt(Math.max(min, 0));
  const sqrtMax = Math.sqrt(max);
  const sqrtVal = Math.sqrt(Math.max(value, 0));
  const normalized = sqrtMax > sqrtMin ? (sqrtVal - sqrtMin) / (sqrtMax - sqrtMin) : 0.5;
  // Output range 124–200px: 124px minimum per design spec; 200 caps at ~half the container.
  return Math.round(124 + normalized * 76);
}

function formatDollar(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

// ─── node metadata builder ────────────────────────────────────────────────────

export function buildBubbleNodeMeta(
  data: BubbleNodeInput[],
  expandedNodeId: string | undefined,
  theme: BubbleChartConfig['theme'],
): BubbleNodeMeta[] {
  const [minVal, maxVal] = getValueRange(data);
  const topLevelTotal = data.reduce((sum, n) => sum + n.value, 0);
  const { fillColors, borderColors, darkColors } = theme;
  const metas: BubbleNodeMeta[] = [];

  data.forEach((node) => {
    const colorIndex = node.colorIndex ?? 0;
    metas.push({
      id: node.id,
      name: node.name ?? node.id,
      symbolSize: scaleBubbleSymbolSize(node.value, minVal, maxVal),
      hasChildren: Array.isArray(node.children) && node.children.length > 0,
      isChild: false,
      isVisible: true,
      displayAmount: formatDollar(node.value),
      pct: Math.round((node.value / topLevelTotal) * 100),
      fillColor: fillColors[colorIndex % fillColors.length],
      borderColor: borderColors[colorIndex % borderColors.length],
      darkColor: darkColors[colorIndex % darkColors.length],
    });

    if (Array.isArray(node.children) && node.children.length > 0) {
      const childTotal = node.children.reduce((sum, c) => sum + c.value, 0);
      node.children.forEach((child) => {
        const ci = child.colorIndex ?? colorIndex;
        metas.push({
          id: child.id,
          name: child.name ?? child.id,
          symbolSize: scaleBubbleSymbolSize(child.value, minVal, maxVal),
          hasChildren: false,
          isChild: true,
          isVisible: expandedNodeId === node.id,
          displayAmount: formatDollar(child.value),
          pct: Math.round((child.value / childTotal) * 100),
          fillColor: fillColors[ci % fillColors.length],
          borderColor: borderColors[ci % borderColors.length],
          darkColor: darkColors[ci % darkColors.length],
        });
      });
    }
  });

  return metas;
}

// ─── ECharts option builder ───────────────────────────────────────────────────

type SimPosition = { id: string; x?: number; y?: number };

// Extra symbol diameter a selected bubble gets. The simulation reserves half of this as frame
// padding so selecting an edge bubble can't push it outside the chart (SIMPL-34407).
export const SELECTED_SYMBOL_GROWTH = 8;

// Drawn width of the arms connecting an expanded parent to its children. Shared with the simulation,
// whose arm-collision geometry must not drift from what is actually painted (SIMPL-34406).
export const BUBBLE_ARM_WIDTH = 6;

// Series ids let per-tick updates target each series without restating its config.
export const BUBBLE_SERIES_IDS = {
  links: 'bubble-links',
  background: 'bubble-background',
  bubbles: 'bubble-nodes',
} as const;

export function buildBubbleSeriesData(
  metas: BubbleNodeMeta[],
  positions: SimPosition[],
  data: BubbleNodeInput[],
  config: BubbleChartConfig,
) {
  const { expandedNodeId, selectedId, containerWidth, containerHeight, theme, privacyMask } = config;
  const posById = new Map(positions.map((n) => [n.id, { x: n.x ?? 0, y: n.y ?? 0 }]));

  const expandedChildren = new Set<string>();
  if (expandedNodeId) {
    const expandedParent = data.find((n) => n.id === expandedNodeId);
    if (Array.isArray(expandedParent?.children)) expandedParent.children.forEach((c) => expandedChildren.add(c.id));
  }

  const isHighlighted = (id: string) => {
    if (!selectedId) return true;
    if (id === selectedId) return true;
    // When the expanded parent itself is selected, its children are also highlighted.
    // When a child is selected the parent becomes inactive (fades) per design spec.
    if (expandedChildren.has(id) && selectedId === expandedNodeId) return true;
    return false;
  };
  const scatterData = metas
    .filter((m) => m.isVisible)
    .map((m) => {
      const pos = posById.get(m.id) ?? { x: containerWidth / 2, y: containerHeight / 2 };
      const isSelected = m.id === selectedId;
      const symbolSize = isSelected ? m.symbolSize + SELECTED_SYMBOL_GROWTH : m.symbolSize;
      return {
        id: m.id,
        name: m.name,
        value: [pos.x, pos.y] as [number, number],
        symbolSize,
        hasChildren: m.hasChildren,
        isChild: m.isChild,
        // Privacy Mode masks the amount only — the category name and percentage stay visible,
        // matching the amCharts label (FreeToSpendNode masked just the balance). Masking here
        // rather than in the label formatter keeps the formatter in the static option, so a
        // privacy toggle only needs a data update.
        displayAmount: privacyMask ? privacyMask(m.displayAmount) : m.displayAmount,
        pct: m.pct,
        itemStyle: {
          color: m.fillColor,
          borderColor: m.borderColor,
          borderWidth: isSelected ? 3 : 1,
          opacity: isHighlighted(m.id) ? 1 : 0.5,
          ...(isSelected ? theme.shadowStrong : {}),
        },
        label: { color: m.darkColor },
        rippleEffect: { color: m.borderColor },
      };
    });

  const backgroundData = metas
    .filter((m) => m.isVisible)
    .map((m) => {
      const pos = posById.get(m.id) ?? { x: containerWidth / 2, y: containerHeight / 2 };
      return { value: [pos.x, pos.y] as [number, number], symbolSize: m.symbolSize };
    });

  const linesData: Array<{ coords: [[number, number], [number, number]]; lineStyle: { color: string } }> = [];
  if (expandedNodeId) {
    const parent = data.find((n) => n.id === expandedNodeId);
    const parentPos = posById.get(expandedNodeId);
    const parentMeta = metas.find((m) => m.id === expandedNodeId);
    if (Array.isArray(parent?.children) && parentPos && parentMeta) {
      parent.children.forEach((child) => {
        const childPos = posById.get(child.id);
        if (childPos) {
          linesData.push({
            coords: [
              [parentPos.x, parentPos.y],
              [childPos.x, childPos.y],
            ],
            lineStyle: { color: parentMeta.borderColor },
          });
        }
      });
    }
  }

  return { linesData, backgroundData, scatterData };
}

export type BubbleSeriesData = ReturnType<typeof buildBubbleSeriesData>;

// Per-tick payload. d3-force moves every node on every frame, so the only thing that changes is
// each series' data — the label rich text, axes and emphasis states below are built once by
// `buildBubbleChartOption` and merged into by series id.
export function buildBubbleDataUpdate(
  metas: BubbleNodeMeta[],
  positions: SimPosition[],
  data: BubbleNodeInput[],
  config: BubbleChartConfig,
): EChartsOption {
  const { linesData, backgroundData, scatterData } = buildBubbleSeriesData(metas, positions, data, config);
  return {
    series: [
      { id: BUBBLE_SERIES_IDS.links, data: linesData },
      { id: BUBBLE_SERIES_IDS.background, data: backgroundData },
      { id: BUBBLE_SERIES_IDS.bubbles, data: scatterData },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] as any as EChartsOption['series'],
  };
}

export function buildBubbleChartOption(
  metas: BubbleNodeMeta[],
  positions: SimPosition[],
  data: BubbleNodeInput[],
  config: BubbleChartConfig,
): EChartsOption {
  const { containerWidth, containerHeight, theme } = config;
  const { linesData, backgroundData, scatterData } = buildBubbleSeriesData(metas, positions, data, config);

  return {
    animation: false,
    grid: { left: 0, top: 0, right: 0, bottom: 0, containLabel: false },
    xAxis: { type: 'value', min: 0, max: containerWidth, show: false },
    yAxis: { type: 'value', min: 0, max: containerHeight, show: false, inverse: true },
    series: [
      {
        id: BUBBLE_SERIES_IDS.links,
        type: 'lines' as const,
        coordinateSystem: 'cartesian2d',
        animation: false,
        data: linesData,
        lineStyle: { width: BUBBLE_ARM_WIDTH, opacity: 0.8 },
        polyline: false,
        silent: true,
      },
      {
        id: BUBBLE_SERIES_IDS.background,
        type: 'scatter' as const,
        coordinateSystem: 'cartesian2d',
        animation: false,
        data: backgroundData,
        symbolSize: (_val: unknown, params: any) => params.data.symbolSize,
        itemStyle: { color: theme.paperBackground, borderWidth: 0, opacity: 1 },
        emphasis: { scale: false },
        silent: true,
      },
      {
        id: BUBBLE_SERIES_IDS.bubbles,
        type: 'effectScatter' as const,
        coordinateSystem: 'cartesian2d',
        animation: false,
        showEffectOn: 'emphasis',
        rippleEffect: { period: 1.5, scale: 2.2, brushType: 'stroke' },
        data: scatterData,
        symbolSize: (_val: unknown, params: any) => params.data.symbolSize,
        label: {
          show: true,
          position: 'inside',
          align: 'center',
          verticalAlign: 'middle',
          formatter: (params: any) => {
            const d = params.data;
            // ECharts canvas doesn't support overflow:truncate on rich text blocks;
            // estimate max chars from bubble width (Rubik 16px/500 ≈ 9px/char).
            const maxChars = Math.max(4, Math.floor((d.symbolSize - 16) / 9));
            const name = d.name.length > maxChars ? `${d.name.slice(0, maxChars - 1)}…` : d.name;
            // displayAmount already carries any Privacy Mode masking (see buildBubbleSeriesData).
            return `{name|${name}}\n{amount|${d.displayAmount}}\n{pct|${d.pct}%}`;
          },
          rich: {
            name: {
              fontFamily: theme.fontFamily,
              fontSize: theme.labelTypography.name.fontSize,
              fontWeight: theme.labelTypography.name.fontWeight,
              lineHeight: theme.labelTypography.name.lineHeight,
            },
            amount: {
              fontFamily: theme.fontFamily,
              fontSize: theme.labelTypography.amount.fontSize,
              fontWeight: theme.labelTypography.amount.fontWeight,
              lineHeight: theme.labelTypography.amount.lineHeight,
            },
            pct: {
              fontFamily: theme.fontFamily,
              fontSize: theme.labelTypography.pct.fontSize,
              fontWeight: theme.labelTypography.pct.fontWeight,
              lineHeight: theme.labelTypography.pct.lineHeight,
            },
          },
        },
        emphasis: {
          scale: false,
          itemStyle: {
            borderWidth: 2,
            ...theme.shadowSoft,
          },
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] as any as EChartsOption['series'],
  };
}
